<template>
    <DashboardSnippet v-slot="{
        metrics,
        loading,
        ridersLoading,
        operationsLoading,
        revenueLoading,
        assetsLoading,
    }">
        <div class="p-1">

            <!-- ── Core Stats ───────────────────────────────────────────── -->
            <div class="row g-3 mb-4">
                <div class="col-6 col-md-3">
                    <Counter
                        title="North Star"
                        :value="metrics.north_star ?? 0"
                        icon="fa fa-star"
                        color="text-primary"
                        :loading="operationsLoading"
                    />
                </div>
                <div class="col-6 col-md-3">
                    <Counter
                        title="Daily Swaps"
                        :value="metrics.swaps_count ?? 0"
                        :growth="metrics.swap_count_growth"
                        icon="fa fa-recycle"
                        color="text-success"
                        :loading="operationsLoading"
                    />
                </div>
                <div class="col-6 col-md-3">
                    <Counter
                        title="Riders"
                        :value="metrics.riders_registered ?? 0"
                        :growth="metrics.riders_registered_growth"
                        icon="fa fa-users"
                        color="text-info"
                        :loading="ridersLoading"
                    />
                </div>
                <div class="col-6 col-md-3">
                    <Counter
                        title="Active Riders"
                        :value="metrics.active_riders ?? 0"
                        :growth="metrics.active_riders_growth"
                        icon="fa fa-person-biking"
                        color="text-warning"
                        :loading="ridersLoading"
                    />
                </div>
            </div>

            <!-- ── Revenue Chart + Revenue & Transactions ──────────────── -->
            <div class="row g-3 mb-4">
                <div class="col-12 col-md-8">
                    <div class="dash-card chart-card position-relative">
                        <div class="d-flex justify-content-between align-items-center mb-3">
                            <div>
                                <h6 class="chart-title">Revenue Trends</h6>
                                <p class="chart-subtitle">Daily revenue over selected period</p>
                            </div>
                        </div>
                        <div v-if="revenueLoading" class="card-spinner-overlay">
                            <div class="spinner-border text-success spinner-border-sm" role="status">
                                <span class="visually-hidden">Loading...</span>
                            </div>
                        </div>
                        <MetricChart
                            :data="metrics.daily_transactions ?? []"
                            chart-type="line"
                            x-key="date"
                            y-key="total_amount"
                        />
                    </div>
                </div>
                <div class="col-12 col-md-4 d-flex">
                    <div class="d-flex flex-column gap-3 w-100">
                        <div class="flex-fill">
                            <Counter
                                title="Transactions"
                                :value="metrics.transaction_count ?? 0"
                                :growth="metrics.transaction_count_growth"
                                icon="fa fa-piggy-bank"
                                color="text-warning"
                                :loading="revenueLoading"
                            />
                        </div>
                        <div class="flex-fill">
                            <Counter
                                title="Revenue"
                                :value="metrics.total_revenue ?? 0"
                                :growth="metrics.total_revenue_growth"
                                :money-options="{ prefix: '₦', decimalPlaces: 2 }"
                                icon="fa fa-money-bill-trend-up"
                                color="text-success"
                                :loading="revenueLoading"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <!-- ── Station Banner (station roles only) ─────────────────── -->
            <div v-if="isStationAdmin && $user.workplace" class="station-banner mb-3">
                <div class="station-banner-icon">
                    <i class="fa fa-charging-station"></i>
                </div>
                <div class="station-banner-body">
                    <p class="station-banner-label">Your Station</p>
                    <h6 class="station-banner-name">{{ $user.workplace.title }}</h6>
                </div>
                <span v-if="$user.workplace.type" class="station-banner-type">
                    {{ $user.workplace.type }}
                </span>
                
                    <a v-if="$user.workplace.slug"
                    :href="route('station.page.single', { station: $user.workplace.slug })"
                    class="ms-auto btn btn-sm btn-outline-secondary"
                >
                    <i class="fa fa-arrow-up-right-from-square me-1"></i> View Station
                </a>
            </div>

            <!-- ── Station Admin Asset Cards ───────────────────────────── -->
            <div v-if="isStationAdmin" class="row g-3 mb-4">
                <div class="col-12 col-md-6">
                    <Counter
                        title="Number of Assets"
                        :value="metrics.total_stocks ?? 0"
                        :growth="metrics.total_stocks_growth"
                        icon="fa fa-cog"
                        color="text-success"
                        :loading="assetsLoading"
                    />
                </div>
                <div class="col-12 col-md-6">
                    <Counter
                        title="Batches Assigned"
                        :value="metrics.total_batches ?? 0"
                        :growth="metrics.total_batches_growth"
                        icon="fa fa-recycle"
                        color="text-success"
                        :loading="assetsLoading"
                    />
                </div>
            </div>

            <!-- ── Asset Performance ──────────────────────────────────────
            <div class="row g-3 mb-4">
                <div class="col-12">
                    <div class="dash-card position-relative">
                        <div v-if="assetsLoading" class="card-spinner-overlay">
                            <div class="spinner-border text-success spinner-border-sm" role="status">
                                <span class="visually-hidden">Loading...</span>
                            </div>
                        </div>
                        <PerformanceTable
                            title="Asset Performance"
                            :performance-data="{
                                top:   metrics.top_assets   ?? [],
                                least: metrics.least_assets ?? [],
                            }"
                        />
                    </div>
                </div>
            </div> -->

            <!-- ── Riders Table ─────────────────────────────────────────── -->
            <div class="row g-3 mb-4">
                <div class="col-12">
                    <div class="dash-card position-relative">
                        <div v-if="ridersLoading" class="card-spinner-overlay">
                            <div class="spinner-border text-success spinner-border-sm" role="status">
                                <span class="visually-hidden">Loading...</span>
                            </div>
                        </div>
                        <TableComponent
                            :data="riders"
                            :columns="riderColumns"
                            title="Riders Operations"
                            subtitle="List of riders operating on the platform"
                            :enable-search="true"
                            :dropdown-filters="riderDropdownFilters"
                            :enable-date-filter="true"
                            date-column="created_at"
                        />
                    </div>
                </div>
            </div>

        </div>
    </DashboardSnippet>
</template>

<script lang="ts">
import type Rider from '@/models/Rider'
import DashboardSnippet from './DashboardSnippet.vue'
import Counter from '@/vue/components/Counter.vue'
import MetricChart from '@/vue/components/MetricChart.vue'
import TableComponent from '@/vue/components/TableComponent.vue'
import PerformanceTable from '@/widget/table/PerformanceTable.vue'
import { Component, Vue, toNative } from 'vue-facing-decorator'
import { hasRole, Role } from '@/models/Admin'

@Component({
    components: { DashboardSnippet, Counter, MetricChart, TableComponent, PerformanceTable },
})
class SuperAdmin extends Vue {
    riders: Rider[] = []
    ridersLoading   = false

    get isStationAdmin(): boolean {
        return hasRole(this.$user, Role.STATION_MANAGER)
            || hasRole(this.$user, Role.STATION_OFFICER)
    }

    get riderColumns() {
        return [
            { key: 'firstname',  label: 'Firstname', type: 'text', sortable: true,  searchable: true  },
            { key: 'lastname',   label: 'Lastname',  type: 'text', sortable: true,  searchable: true  },
            { key: 'engine',     label: 'Engine',    type: 'text', sortable: true,  searchable: false },
            { key: 'created_at', label: 'Created',   type: 'date', sortable: true,  searchable: false },
        ]
    }

    get riderDropdownFilters() {
        return [{
            key: 'status',
            label: 'Status',
            options: [
                { value: 'active',    label: 'Active'    },
                { value: 'inactive',  label: 'Inactive'  },
                { value: 'suspended', label: 'Suspended' },
            ],
            multiple: false,
            placeholder: 'All Statuses',
        }]
    }

    async fetchRiders(): Promise<void> {
        this.ridersLoading = true
        try {
            const { data } = await this.$http.get(this.route('rider.ajax.active'))
            this.riders = data.riders
        } catch (e) {
            console.error('SuperAdmin — fetchRiders error:', e)
        } finally {
            this.ridersLoading = false
        }
    }

    mounted(): void {
        this.fetchRiders()
    }
}

export default toNative(SuperAdmin)
</script>

<style lang="scss" scoped>
.dash-card {
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    padding: 1.25rem 1.5rem;
    box-shadow: 0 1px 3px rgba(0,0,0,0.04);
    transition: box-shadow 0.2s;
    &:hover { box-shadow: 0 4px 12px rgba(0,0,0,0.08); }
}

.chart-card    { min-height: 340px; }
.chart-title   { font-size: 0.95rem; font-weight: 600; color: #111827; margin: 0; }
.chart-subtitle { font-size: 0.78rem; color: #9ca3af; margin: 0; }

.card-spinner-overlay {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255,255,255,0.7);
    border-radius: 12px;
    z-index: 10;
}

.station-banner {
    display: flex;
    align-items: center;
    gap: 1rem;
    background: #fff;
    border: 1px solid #e5e7eb;
    border-left: 4px solid #3D5A00;
    border-radius: 10px;
    padding: 0.875rem 1.25rem;
    box-shadow: 0 1px 3px rgba(0,0,0,0.04);
    flex-wrap: wrap;
}

.station-banner-icon {
    flex-shrink: 0;
    width: 40px;
    height: 40px;
    border-radius: 8px;
    background: #f0f4e6;
    color: #3D5A00;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1rem;
}

.station-banner-body { display: flex; flex-direction: column; gap: 1px; }
.station-banner-label { font-size: 0.68rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.07em; color: #9ca3af; margin: 0; }
.station-banner-name  { font-size: 0.95rem; font-weight: 600; color: #111827; margin: 0; }

.station-banner-type {
    display: inline-flex;
    align-items: center;
    padding: 0.2rem 0.65rem;
    border-radius: 999px;
    font-size: 0.72rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    background: #dcfce7;
    color: #15803d;
}
</style>